import { DOCUMENTS_OVERDUE } from "./requiredDocsLock";

// Submission-safety helpers for the provider/manufacturer peptide checkout.
//
// The provider checkout (PeptideCheckout.handleConfirmedSubmit) had no
// idempotency: every failed-charge retry created a fresh payment_pending order,
// and a charge that succeeded server-side but whose response was lost (timeout →
// the sanitized "charge_network_error") let the user retry and double-charge.
// These helpers give that flow the same protection the patient checkout already
// has: a stable fingerprint of the order so a retry reuses the existing pending
// order instead of minting a new one, plus a post-error reconciliation check.

// Stable fingerprint over everything that defines "this exact order": the line
// items (variant/qty/price/form, order-independent), the money, where it ships,
// who it's for, and how it's paid. Any change mints a new fingerprint so a
// corrected ZIP or an added line creates a new order rather than reusing a
// stale one — mirrors the patient checkout's idempotency-scope reasoning.
export function buildOrderFingerprint(payload) {
  if (!payload) return "";
  const items = Array.isArray(payload.items) ? payload.items : [];
  const itemKey = items
    .map(
      (it) =>
        `${it.variantId}:${it.quantity}:${it.priceCents}:${it.customPriceCents ?? ""}:${it.formType ?? ""}:${it.requestedFulfillment ?? ""}`
    )
    .sort()
    .join("|");
  const s = payload.shipping || {};
  const shipKey = [s.firstName, s.lastName, s.name, s.email, s.phone, s.address, s.city, s.state, s.zip]
    .map((v) => v ?? "")
    .join(":");
  const targetKey = [
    payload.assignType ?? "",
    payload.assignedProviderId ?? "",
    payload.assignedPatientId ?? "",
    payload.fulfillmentType ?? "",
    payload.paymentMethod ?? "",
    // Shipping method changes the server-resolved fee (and thus the charge)
    // while leaving the product-only totalCents identical — without this a
    // method switch between attempts would silently reuse the prior order
    // and charge the OLD fee.
    payload.shippingMethodId ?? "",
  ].join(":");
  return `${itemKey}#${payload.totalCents ?? ""}#${shipKey}#${targetKey}`;
}

// Reuse the prior pending order only when nothing about the order changed since
// it was created. `pending` is { orderId, fingerprint } cached from the last
// create attempt; falsy orderId or a fingerprint mismatch means "create fresh."
export function shouldReuseOrder(pending, fingerprint) {
  if (!pending || !pending.orderId) return false;
  return pending.fingerprint === fingerprint;
}

// chargePeptideOrderCard sanitizes every transport failure to this single
// message (never rethrows the axios error, which can carry card data). A
// network error means the charge's outcome is UNKNOWN — the charge may have
// landed server-side — so the caller must reconcile against the order rather
// than blindly let the user retry.
export function isChargeRecoverableError(err) {
  return !!err && err.message === "charge_network_error";
}

// After a recoverable charge error, re-fetch the order: if the backend already
// captured it, treat the submission as successful instead of letting the user
// charge again. `confirmed` is the post-charge status; paidAt/everPaid are the
// authoritative paid markers the analytics layer already relies on.
export function didOrderActuallyCharge(order) {
  if (!order) return false;
  if (order.status === "confirmed") return true;
  if (order.paidAt) return true;
  if (order.everPaid === true) return true;
  return false;
}

// Step 6b (backend #172): the order-create process atomically REFUSES an order
// whose lines violate the manufacturer's per-SKU back-order policy. It returns a
// clean 200 with { blocked: true, errorCode, errorMessage, blockedLines } and NO
// `order`. Without this, createPeptideOrder returned `undefined` and the checkout
// crashed on `order.id`, surfacing only a generic "error placing order" toast —
// the prescriber never learned a SKU was sold out / discontinued / over its
// back-order cap. These helpers let createPeptideOrder raise a typed error the
// checkout can translate into the specific reason. `code` is matched (never the
// message) so it survives the outer catch's message-only logging.
export const PEPTIDE_ORDER_BLOCKED = "peptide_order_blocked";

export function isOrderBlockedOutput(output) {
  return !!output && output.blocked === true;
}

export function makeBlockedOrderError(output) {
  const err = new Error(PEPTIDE_ORDER_BLOCKED);
  err.code = PEPTIDE_ORDER_BLOCKED;
  err.blockedReason = (output && output.errorCode) || null; // discontinued | sold_out | cap_reached
  err.blockedMessage = (output && output.errorMessage) || null;
  err.blockedLines = (output && output.blockedLines) || [];
  return err;
}

export function isOrderBlockedError(err) {
  return !!err && err.code === PEPTIDE_ORDER_BLOCKED;
}

// Single source of truth for blocked-order errorCode → localized toast key.
// Shared by the provider/manufacturer checkout AND the patient checkout so
// the two maps can't drift when a new backend block code lands. Backend
// emitters: back-order policy (summarizeBlocked) and the shipping-method
// gates (lib/peptideShipping.js in lucasai-backend).
export const PEPTIDE_BLOCKED_TOAST_KEYS = {
  // Required-documents lock. The provider checkout routes this reason through
  // DOCUMENTS_OVERDUE_EVENT (locked prompt, no toast); this entry is only the
  // fallback for any other caller of the map.
  [DOCUMENTS_OVERDUE]: "common.requiredDocsLock.orderBlocked",
  discontinued: "peptides.toasts.orderBlockedDiscontinued",
  sold_out: "peptides.toasts.orderBlockedSoldOut",
  cap_reached: "peptides.toasts.orderBlockedCapReached",
  shipping_method_required: "peptides.toasts.selectShippingMethod",
  shipping_method_invalid: "peptides.toasts.shippingMethodInvalid",
  shipping_method_unavailable_state: "peptides.toasts.shippingMethodUnavailableState",
};
